import {t, plural, select} from "@lingui/core/macro";
import {includes, isNumber} from "lodash";

import {matches, lengthInterval} from "@atg/utils/validators";
import {parseDateTimestamp} from "@atg-shared/datetime";
import {serverTime} from "@atg-shared/server-time";
import {RoundState, MembershipPolicy, TeamType} from "@atg-tillsammans/types";
import {formatGameTypes, GameUtils} from "@atg-tillsammans-shared/utils";

import type {ValidationResult} from "@atg/utils/validators";
import type {MemberTeam, Team, TeamGame, TeamID} from "@atg-tillsammans/types";
import type {Round, Participant, Game as GameGQL} from "@atg-tillsammans/types/generated";
import type {GameType, Game as GameShared} from "@atg-tillsammans-shared/types";

type Game = GameShared | GameGQL;

export const isRoundActivated = (state: RoundState) =>
    includes(
        [
            RoundState.OPEN,
            RoundState.SOLD_OUT,
            RoundState.NOT_PLACED,
            RoundState.CLOSED,
            RoundState.PLACED,
        ],
        state,
    );

export function getNumberOfBoughtSharesLabel(participation?: Participant): string {
    if (!participation) return "";

    return plural(participation.numberOfShares, {
        one: "# andel",
        other: "# andelar",
        many: "# andelar",
    });
}

export function getGameTypeWithDayLabel(game: Game): string {
    return `${game?.gameId?.gameType} ${parseDateTimestamp(
        game.scheduledStartTime ?? "",
    ).format("dddd")}`;
}

export function hasBuyDeadlinePassed(round?: Round): boolean {
    const deadline = round?.sales?.deadline;
    return deadline ? serverTime().isAfter(parseDateTimestamp(deadline)) : true;
}

export function hasGameRoundStarted(gameRound: Game): boolean {
    return serverTime().isAfter(parseDateTimestamp(gameRound.scheduledStartTime ?? ""));
}

export function getMemberAmountLabel({memberAmount}: MemberTeam): string | null {
    if (!isNumber(memberAmount)) return null;
    return plural(memberAmount, {
        one: `# medlem`,
        other: `# medlemmar`,
    });
}

export function isHistorical(gameRound: Game): boolean {
    return serverTime().isAfter(parseDateTimestamp(gameRound.endTime ?? ""));
}

export const hasJackpot = (game: TeamGame): boolean => GameUtils.hasJackpot(game);

export const getTeamGameTypeText = (gameTypes: GameType[]): string => {
    const tillsammansGameTypes = GameUtils.getSelectableGameTypes();

    const currentGameTypes = gameTypes.filter((gameType) =>
        tillsammansGameTypes.includes(gameType),
    );

    const fullMatch = tillsammansGameTypes.every((gameType) =>
        currentGameTypes.includes(gameType),
    );

    if (currentGameTypes.length === 0 || fullMatch) {
        return t`Laget spelar alla spelformer.`;
    }

    if (currentGameTypes.length >= tillsammansGameTypes.length) {
        return getTeamGameTypeText(currentGameTypes);
    }

    return currentGameTypes.length === 1
        ? formatGameTypes([currentGameTypes[0]])
        : formatGameTypes(currentGameTypes);
};

const getMembershipPolicyText = (membershipPolicy: MembershipPolicy): string =>
    select(membershipPolicy, {
        CLOSED: "Slutet lag",
        OPEN: "Öppet lag",
        PRIVATE: "Privat lag",
        other: "",
    });

export const getTeamTypeTextInternal = (
    teamType: TeamType | null,
    membershipPolicy: MembershipPolicy | null,
): string => {
    if (teamType === null || !membershipPolicy) {
        return "";
    }

    switch (teamType) {
        case TeamType.CAMPAIGN:
            return t({id: "team.utils.TeamType.CAMPAIGN", message: "Kampanjlag"});
        case TeamType.BEGINNER:
            return t({id: "team.utils.TeamType.BEGINNER", message: "Nybörjarlag"});
        case TeamType.STANDARD:
            return getMembershipPolicyText(membershipPolicy);
        default:
            return "";
    }
};

export const getTeamTypeDescriptionInternal = (
    teamType: TeamType | null,
    membershipPolicy: MembershipPolicy | null,
) => {
    if (teamType === null || teamType !== TeamType.STANDARD) {
        return undefined;
    }

    switch (membershipPolicy) {
        case MembershipPolicy.CLOSED:
            return t({
                id: "team.utils.MembershipPolicy.CLOSED",
                message: `Laget är sökbart under "Hitta lag". Vem som helst kan ansöka om att gå med i laget.`,
            });
        case MembershipPolicy.OPEN:
            return t({
                id: "team.utils.MembershipPolicy.OPEN",
                message: `Laget är sökbart under "Hitta lag". Vem som helst kan gå med i laget.`,
            });
        case MembershipPolicy.PRIVATE:
            return t({
                id: "team.utils.MembershipPolicy.PRIVATE",
                message: `Laget är inte sökbart under "Hitta lag". Endast inbjudna till laget kan gå med.`,
            });

        default:
            return null;
    }
};

export const teamNameValidator = (value: string): ValidationResult => {
    const result = lengthInterval(5, 45)(value);
    return result.failed
        ? result
        : matches(
              // eslint-disable-next-line prefer-regex-literals
              new RegExp("^[a-zA-ZåäöÅÄÖ0-9\\s,\\.\\-\\?\\!\\']+$"),
              t`Ange ett godkänt lagnamn. (Godkänt a-ö,A-Ö,0-9 samt , . - ? ! ')`,
          )(value);
};

export const hasNumbersOnly = (value: string): boolean => {
    const numberPattern = /^-?\d+$/;

    if (numberPattern.test(value)) {
        return true;
    }

    return false;
};

export const getAllMembershipPolicies = (): MembershipPolicy[] => [
    MembershipPolicy.PRIVATE,
    MembershipPolicy.CLOSED,
    MembershipPolicy.OPEN,
];

export const getMembershipPolicySelectOptions = () => [
    {
        label: t`Privat`,
        value: MembershipPolicy.PRIVATE,
        description: t`Endast inbjudna kan gå med i laget. Laget syns inte i sökningar.`,
    },
    {
        label: t`Slutet`,
        value: MembershipPolicy.CLOSED,
        description: t`Alla kan hitta laget men behöver ansöka om att gå med.`,
    },
    {
        label: t`Öppet`,
        value: MembershipPolicy.OPEN,
        description: t`Alla kan hitta och gå med i laget direkt.`,
    },
];

export const getSystemLimitDescription = () =>
    t`En spelare kan delta i upp till 5000 system per omgång. Som lagkapten kan du sätta en egen gräns för hur många system laget får lämna in.`;

/** Utility functions previously found in the TeamModel. */

export const toTeamId = (teamId: string): TeamID => teamId as TeamID;

export const formatFullName = (member: {firstName?: string; lastName?: string}): string =>
    `${member.firstName} ${member.lastName}`;

export const isBeginnerTeam = (type: TeamType): boolean => type === TeamType.BEGINNER;

export const isCampaignTeam = (type: TeamType): boolean => type === TeamType.CAMPAIGN;

export const isStandardTeam = (type: TeamType): boolean => type === TeamType.STANDARD;

export const isNotStandardTeam = (type: TeamType): boolean => !isStandardTeam(type);

export const getDefaultGameTypeOf = (
    team: Team,
    gameTypes: GameType[],
): GameType | undefined => {
    const teamGameTypes = team.gameTypes ?? [];
    return gameTypes.find((type) => teamGameTypes.includes(type));
};
